import type { CustomerMonthRow, CustomerSeries } from '@/typings/CustomerChart'

export const primaryChartColor = '#058880'
export const otherCustomersColor = '#c8c8c8'
export const otherCustomersLabel = 'Other customers'

// Based on the primary petrol, highlight turquoise and secondary orange in
// https://www.21grams.com/ and its 21grams-theme stylesheet. Supporting tones
// keep customer series within those color families; neutral grey is for Other.
const customerPalette = [
  '#034440',
  '#80cfcc',
  '#c86d44',
  '#93abab',
  '#0b6862',
  '#ffb89e',
  '#0eb8b3',
  '#73938f',
  '#ff4e10',
  '#8caba8',
  '#5a8080',
  '#dda184',
  '#012b2b',
  primaryChartColor,
  '#479f99',
  '#bc785b',
] as const

// Customer identity, rather than rank or response order, determines the color.
// Normalize display-name casing/spacing without combining separate data series.
export function customerColor(name: string): string {
  const identity = name.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase()
  let hash = 2166136261
  for (const character of identity) {
    hash = Math.imul(hash ^ character.charCodeAt(0), 16777619)
  }
  // Mix all bits before selecting a palette entry so similar names do not
  // cluster in the same small subset of brand colors.
  hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b)
  hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35)
  hash = (hash ^ (hash >>> 16)) >>> 0
  const base = customerPalette[hash % customerPalette.length]
  // Add modest tonal variation for customers sharing a palette entry without
  // introducing hues outside the brand's color families.
  const tint = ((hash >>> 4) / 0x0fffffff) * 0.22
  const channels = [1, 3, 5].map((offset) => {
    const value = Number.parseInt(base.slice(offset, offset + 2), 16)
    return Math.round(value + (255 - value) * tint)
      .toString(16)
      .padStart(2, '0')
  })
  return '#' + channels.join('')
}

export function monthlyCustomerRows(series: CustomerSeries[], month: number): CustomerMonthRow[] {
  const active = series
    .map((customer) => ({
      name: customer.name,
      value: customer.data[month] ?? 0,
      color: customerColor(customer.name),
    }))
    .filter((customer) => customer.value !== 0)
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name))
  const leaders: CustomerMonthRow[] = active.slice(0, 5)
  const others = active.slice(5)
  if (others.length) {
    leaders.push({
      name: `${otherCustomersLabel} (${others.length})`,
      value: others.reduce((total, customer) => total + customer.value, 0),
      color: otherCustomersColor,
      grouped: true,
    })
  }
  return leaders
}

export function monthlyCustomerSeries(series: CustomerSeries[], months: CustomerMonthRow[][]): CustomerSeries[] {
  // Keep a consistent stack order, but only include a customer's value when
  // that customer is in this month's top five. Everything else belongs to Other.
  const leaders = months.map((month) => new Set(month.filter((row) => !row.grouped).map((row) => row.name)))
  const visible: CustomerSeries[] = series
    .filter((customer) => leaders.some((month) => month.has(customer.name)))
    .map((customer) => ({
      name: customer.name,
      data: months.map((_, index) => (leaders[index].has(customer.name) ? (customer.data[index] ?? 0) : 0)),
    }))
  if (months.some((month) => month.some((row) => row.grouped))) {
    visible.push({
      name: otherCustomersLabel,
      data: months.map((month) => month.find((row) => row.grouped)?.value ?? 0),
      grouped: true,
    })
  }
  return visible
}
